import React, { useMemo, useState } from "react";
import dayjs from "dayjs";
import { v4 as uuidv4 } from "uuid";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";

import { MessageSquare, Send, Trash2, User, Calendar } from "lucide-react";
import type { PersonModelInterface } from "@/models/personModel";

export type PersonComment = {
    id: string;
    text: string;
    author: string;
    createdAt: string; // ISO string
};

type Props = {
    person: PersonModelInterface;
    onUpdatePerson: (next: PersonModelInterface) => Promise<void>;
    disabled?: boolean; // например, когда closed/closedEarly
    currentUserName?: string; // если есть автор
};

function safeString(v: unknown) {
    return typeof v === "string" ? v : "";
}

function asComments(raw: unknown): PersonComment[] {
    if (!Array.isArray(raw)) return [];
    return raw
        .map((x: any) => ({
            id: safeString(x?.id),
            text: safeString(x?.text),
            author: safeString(x?.author) || "Менеджер",
            createdAt: safeString(x?.createdAt) || new Date().toISOString(),
        }))
        .filter((c) => c.id && c.text);
}

export function CommentsCard({
                                 person,
                                 onUpdatePerson,
                                 disabled = false,
                                 currentUserName = "Менеджер",
                             }: Props) {
    const comments = useMemo(() => asComments((person as any).comments), [person]);

    const [text, setText] = useState("");
    const [isSaving, setIsSaving] = useState(false);
    const [deletingId, setDeletingId] = useState<string | null>(null);

    const sorted = useMemo(() => {
        return [...comments].sort((a, b) => {
            const av = dayjs(a.createdAt).valueOf();
            const bv = dayjs(b.createdAt).valueOf();
            return bv - av;
        });
    }, [comments]);

    async function addComment() {
        const trimmed = text.trim();
        if (!trimmed || disabled || isSaving) return;

        setIsSaving(true);
        try {
            const nextComment: PersonComment = {
                id: uuidv4(),
                text: trimmed,
                author: currentUserName,
                createdAt: new Date().toISOString(),
            };

            const nextPerson: PersonModelInterface = {
                ...person,
                comments: [...comments, nextComment] as any,
            };

            await onUpdatePerson(nextPerson);
            setText("");
        } finally {
            setIsSaving(false);
        }
    }

    async function deleteComment(id: string) {
        if (disabled || deletingId) return;
        setDeletingId(id);
        try {
            const nextPerson: PersonModelInterface = {
                ...person,
                comments: comments.filter((c) => c.id !== id) as any,
            };
            await onUpdatePerson(nextPerson);
        } finally {
            setDeletingId(null);
        }
    }

    return (
        <Card>
            <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <MessageSquare className="h-4 w-4" />
                    Комментарии
                </CardTitle>
            </CardHeader>

            <CardContent className="space-y-4">
                <div className="space-y-3 max-h-[260px] overflow-y-auto pr-1">
                    {sorted.length === 0 ? (
                        <div className="text-sm text-muted-foreground text-center py-6">
                            Нет комментариев
                        </div>
                    ) : (
                        sorted.map((c) => (
                            <div key={c.id} className="rounded-xl border bg-muted/30 p-3">
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <div className="flex flex-wrap items-center gap-3">
                                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                                <User className="h-3.5 w-3.5" />
                                                <span className="font-medium text-foreground">{c.author}</span>
                                            </div>

                                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                                <Calendar className="h-3.5 w-3.5" />
                                                <span>{dayjs(c.createdAt).format("DD.MM.YYYY HH:mm")}</span>
                                            </div>
                                        </div>

                                        <Separator className="my-2" />

                                        <p className="text-sm whitespace-pre-wrap break-words">{c.text}</p>
                                    </div>

                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        className="shrink-0 text-destructive hover:text-destructive"
                                        onClick={() => deleteComment(c.id)}
                                        disabled={disabled || deletingId === c.id}
                                        title="Удалить"
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>
                        ))
                    )}
                </div>

                <div className="flex gap-2">
                    <Textarea
                        placeholder="Написать комментарий"
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        className="min-h-[70px]"
                        disabled={disabled || isSaving}
                    />
                    <Button
                        type="button"
                        size="icon"
                        onClick={addComment}
                        disabled={disabled || isSaving || !text.trim()}
                        title="Отправить"
                    >
                        <Send className="h-4 w-4" />
                    </Button>
                </div>
            </CardContent>
        </Card>
    );
}
